import React, { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import Insights from "@mui/icons-material/Insights";
import PieChartIcon from "@mui/icons-material/PieChart";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Dialog from "components/dialog";
import { useLazyGetAiVisualizationQuery } from "services/ai";
import {
  AiVisualizationRequestBody,
  VisualizationCharts
} from "services/types/ai.type";

const AiVisualizationGenerator = () => {
  const [isOpen, setIsOpen] = useState(false);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { setValue, control, watch } = useForm<AiVisualizationRequestBody>({
    defaultValues: { question: "", chart_type: VISUALIZATION_TYPES[0].value }
  });

  const [question, chart_type] = watch<["question", "chart_type"]>([
    "question",
    "chart_type"
  ]);

  const [getAiVisualization, { currentData, isFetching }] =
    useLazyGetAiVisualizationQuery();

  const visualizationData = currentData?.result.data || [];
  const visualizationImage = currentData?.result.image;

  const generateChart = () => getAiVisualization({ question, chart_type });

  return (
    <>
      <IconButton onClick={onOpen}>
        <Insights />
      </IconButton>
      <Dialog
        title="AI Visualization Generator"
        onClose={onClose}
        open={isOpen}
        maxWidth="lg"
        dialogContentProps={{ sx: { pb: 5 } }}
      >
        <Stack gap={4}>
          <Stack gap={2}>
            <Typography variant="h5" fontSize={18} component="div">
              Starters
            </Typography>

            <Grid display="grid" gap={3} gridTemplateColumns="1fr 1fr">
              {STARTERS.map(({ title, description }) => (
                <Card key={title} sx={{ borderRadius: 2 }}>
                  <CardActionArea
                    onClick={() => {
                      setValue("question", description);
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" component="div">
                        {title}
                      </Typography>
                      <Typography maxWidth={250} marginTop={1}>
                        {description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Grid>
          </Stack>
          <Stack gap={2}>
            <Controller
              name="question"
              control={control}
              render={({ field }) => (
                <TextField
                  label="What would you like to generate a visualization of?"
                  variant="outlined"
                  multiline
                  fullWidth
                  minRows={2}
                  maxRows={5}
                  {...field}
                />
              )}
            />
            <Stack flexDirection="row" gap={1} justifyContent="space-between">
              {VISUALIZATION_TYPES.map(({ icon, title, value }) => {
                return (
                  <Button
                    key={title}
                    onClick={() => {
                      setValue("chart_type", value);
                    }}
                    variant={chart_type === value ? "contained" : "outlined"}
                    startIcon={icon}
                    value={title}
                  >
                    {title}
                  </Button>
                );
              })}
            </Stack>

            <LoadingButton
              loading={isFetching}
              disabled={!question || !chart_type}
              onClick={generateChart}
              variant="contained"
              startIcon={<AutoFixHighIcon />}
            >
              Generate visualization
            </LoadingButton>
          </Stack>

          {visualizationData?.length === 1 ? (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" component="div">
                  Generation result
                </Typography>
                <Typography variant="h5" marginTop={1}>
                  {Object.values(visualizationData[0]).join(": ")}
                </Typography>
              </CardContent>
            </Card>
          ) : visualizationImage ? (
            <Box
              width={600}
              component="img"
              src={`data:image/jpeg;base64,${visualizationImage}`}
            />
          ) : null}
        </Stack>
      </Dialog>
    </>
  );
};

const VISUALIZATION_TYPES = [
  // {
  //   icon: <TableChartOutlinedIcon />,
  //   title: "Table",
  //   value: VisualizationCharts.table,
  // },
  {
    icon: <ShowChartOutlinedIcon />,
    title: "Line",
    value: VisualizationCharts.lineChart
  },
  {
    icon: <BarChartOutlinedIcon />,
    title: "Bar",
    value: VisualizationCharts.barChart
  },
  { icon: <PieChartIcon />, title: "Pie", value: VisualizationCharts.pieChart },
  {
    icon: <CropOriginalIcon />,
    title: "Area",
    value: VisualizationCharts.areaChart
  },
  {
    icon: <ScatterPlotIcon />,
    title: "Scatter",
    value: VisualizationCharts.scatterChart
  }
];

const STARTERS = [
  {
    title: "Appointments",
    description: "What is my breakdown of appointments by type this year?"
  },
  {
    title: "Sales",
    description: "What is my year to date monthly revenue?"
  },
  {
    title: "Patients",
    description:
      "How many new patients this year were referred by existing patients?"
  },
  {
    title: "Staff",
    description: "Which staff member generated the most revenue last quarter?"
  }
];

export default AiVisualizationGenerator;
